import React, { useState } from 'react';
import { Volume2Icon, WavesIcon } from 'lucide-react';
import { SectionHeading, SpecSection } from './SectionHeading';
import { AnnaAvatar } from '../avatar/AnnaAvatar';
import { AlivenessDemo } from './AlivenessDemo';
import { avatarStates } from '../../data/avatarStates';
import { getAgent } from '../../utils/agents';
import { avatarThreads } from '../../utils/dbSelectors';
import { playSound } from '../../utils/sound';
import { useDb } from '../../hooks/useDb';
import { useSpecSettings } from '../../contexts/SpecSettingsContext';
import type { AvatarStateSpec } from '../../types/anna';
function StateFrame({
  spec
}: {
  spec: AvatarStateSpec;
}) {
  const [pulse, setPulse] = useState(0);
  const {
    soundOn
  } = useSpecSettings();
  const threads = useDb(avatarThreads);
  const busy = useDb(t => t.agentTasks.filter(a => a.status === 'bezig'));
  return <article className="flex flex-col" data-id="element-1126" data-mp="2m7b8fzpe8oba">
      <div className="relative grid aspect-[4/3] place-items-center overflow-hidden rounded-2xl border border-white/[0.06] bg-night-900" data-id="element-1127" data-mp="2m8cuez7y6v7s">
        <div className="aspect-square h-full" data-id="element-1128" data-mp="2gcm4k468ft48">
          <AnnaAvatar state={spec.id} threads={threads} pulseKey={pulse} data-id="element-1129" data-mp="2kcw9rem7afi1" />
        </div>
        {spec.id === 'meldt' && <button type="button" onClick={() => {
        setPulse(p => p + 1);
        if (soundOn) playSound('meldt');
      }} className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-white/[0.07] px-3 py-1.5 text-[12px] font-medium text-ink-soft transition-colors duration-150 hover:bg-white/[0.12] hover:text-ink" data-id="element-1130" data-mp="26dmymtcuqbkx">
            <WavesIcon className="h-3.5 w-3.5" aria-hidden data-id="element-1131" data-mp="2ua0ynm7fkp2j" /> Golf afspelen
          </button>}
        {spec.id === 'werkt' && <div className="absolute bottom-3 left-3 flex flex-wrap gap-3" data-id="element-1132" data-mp="3hyk84527v7w7">
            {busy.map(t => {
          const a = getAgent(t.agent);
          return <span key={t.id} className="flex items-center gap-1.5 text-[12px] text-ink-muted" data-id="element-1133" data-mp="hfzyr0wyh6f">
                  <span className="h-1.5 w-1.5 rounded-full" style={{
              backgroundColor: a.color
            }} data-id="element-1134" data-mp="3rmpixyqhpv6j" /> {a.name}
                </span>;
        })}
          </div>}
      </div>
      <h3 className="mt-5 text-[18px] font-semibold text-ink" data-id="element-1135" data-mp="3imsxs73b7eg8">{spec.label}</h3>
      <p className="mt-1.5 text-[15px] leading-relaxed text-ink-soft" data-id="element-1136" data-mp="3iovlgkgzovhs">{spec.motion}</p>
      <p className="mt-3 flex items-center gap-2 text-[13px] text-ink-muted" data-id="element-1137" data-mp="3dpj3oe4z6em2">
        <Volume2Icon className="h-3.5 w-3.5 shrink-0" aria-hidden data-id="element-1138" data-mp="38v54s1r3tpcn" /> {spec.sound}
      </p>
    </article>;
}
export function PresenceSection() {
  return <SpecSection id="aanwezigheid" data-id="element-1139" data-mp="2yz20oqcozgn2">
      <SectionHeading id="aanwezigheid" index={2} title="Anna’s aanwezigheid" intro="Een abstracte, levende vorm: geen gezicht, geen cartoon. Een lichtende kern van fijne deeltjes en lichtfilamenten in een zwak bolvormig veld, met diepte door gelaagde gloed en subtiele parallax. Elke toestand heeft een eigen beweging." data-id="element-1140" data-mp="2z04r6x63rh2g" />
      <div className="mt-14 grid gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3" data-id="element-1141" data-mp="1aeedcds3m0qp">
        {avatarStates.map(s => <StateFrame key={s.id} spec={s} data-id="element-1142" data-mp="1ehkjzmj4htxp" />)}
      </div>
      <AlivenessDemo data-id="element-1143" data-mp="1jfc968sdv1y7" />
    </SpecSection>;
}